import * as moment from 'moment';

export enum StoreName {
  Amazon = 'Amazon',
  Bigcommerce = 'Bigcommerce',
  CSV = 'CSV_Import',
  ChannelAdvisor = 'ChannelAdvisor',
  Etsy = 'Etsy',
  Facebook = 'Facebook',
  Godaddy = 'GoDaddy',
  Magento = 'Magento',
  Magento_2 = 'Magento 2',
  Selz = 'Selz',
  Shippo = 'Shippo',
  Shopify = 'Shopify',
  Spreecommerce = 'Spreecommerce',
  Square = 'Square',
  Squarespace = 'Squarespace',
  Stripe = 'Stripe',
  TikTok = 'TikTok',
  Walmart = 'Walmart',
  Weebly = 'Weebly',
  Wix = 'Wix',
  WooCommerce = 'WooCommerce',
  eBay = 'eBay',
  ePages = 'ePages',
}

// https://shippo.atlassian.net/wiki/spaces/PD/pages/1246199929/Order+Splitting
export const StoresEligibleForSplit: Set<string> = new Set([
  StoreName.Shopify,
  StoreName.Amazon,
  StoreName.Bigcommerce,
  StoreName.Magento_2,
  StoreName.ChannelAdvisor,
  StoreName.Wix,
  StoreName.Walmart,
  StoreName.CSV,
  StoreName.Shippo,
  StoreName.TikTok,
]);

export enum StoreStatusName {
  Connected = 'Connected',
  Failed = 'Failed',
  InProgress = 'InProgress',
  Revoked = 'Revoked',
}

type StorePlatform = {
  name: StoreName;
};

type StoreStatus = {
  description: string;
  name: StoreStatusName;
};

type Store = {
  displayName: string;
  // Only used by some stores to go to the edit pages
  editObjectId?: string;
  expirationDate: Maybe<moment.Moment>;
  isAutoFulfill: boolean;
  isFulfillEmailEnabled: boolean;
  isImportPosOrdersEnabled: boolean;
  lastSyncTime: moment.Moment;
  name: Maybe<string>;
  objectCreated: moment.Moment;
  objectId: string;
  status: StoreStatus;
  statusDetails: string;
  storePlatform: StorePlatform;
  website: Maybe<string>;
};

export type jsonStore = {
  display_name?: string;
  expiration_date: Maybe<string>;
  is_auto_fulfill: Maybe<boolean>;
  is_fulfill_email_enabled: Maybe<boolean>;
  is_import_pos_orders_enabled: Maybe<boolean>;
  last_sync_time: string;
  name: Maybe<string>;
  object_created: string;
  object_id: string;
  status: {
    description: string;
    name: string;
  };
  status_details: string;
  store_platform: {
    name: string;
  };
  website: Maybe<string>;
};

export const fromJSON = (jsonStore: jsonStore): Store => {
  // Some stores do not have status object
  // Even if they are connected, so defaulting to a connected status
  const status = jsonStore.status
    ? jsonStore.status
    : {
        description: 'Fallback to connected',
        name: StoreStatusName.Connected,
      };

  return {
    displayName: jsonStore.display_name ?? '',
    expirationDate: jsonStore.expiration_date ? moment(jsonStore.expiration_date) : null,
    isAutoFulfill: jsonStore.is_auto_fulfill || false,
    isFulfillEmailEnabled: jsonStore.is_fulfill_email_enabled || false,
    isImportPosOrdersEnabled: jsonStore.is_import_pos_orders_enabled || false,
    lastSyncTime: moment(jsonStore.last_sync_time),
    name: jsonStore.name,
    objectCreated: moment(jsonStore.object_created),
    objectId: jsonStore.object_id,
    status: {
      description: status.description,
      name: StoreStatusName[status.name as keyof typeof StoreStatusName],
    },
    statusDetails: jsonStore.status_details,
    storePlatform: {
      name: StoreName[jsonStore.store_platform?.name?.replace(' ', '') as keyof typeof StoreName],
    },
    website: jsonStore.website,
  };
};

// this should be in constants/store.ts but is causing a circular dependency
const storesFilterValues = {
  [StoreName.Amazon]: 'Amazon',
  [StoreName.Bigcommerce]: 'Bigcommerce',
  [StoreName.CSV]: 'CSV_Import',
  [StoreName.ChannelAdvisor]: 'ChannelAdvisor',
  [StoreName.Etsy]: 'Etsy',
  [StoreName.Facebook]: 'Facebook',
  [StoreName.Godaddy]: 'Godaddy',
  [StoreName.Magento]: 'Magento',
  [StoreName.Magento_2]: 'Magento_2',
  [StoreName.Shippo]: 'Shippo',
  [StoreName.Shopify]: 'Shopify',
  [StoreName.Spreecommerce]: 'Spree',
  [StoreName.Square]: 'Square',
  [StoreName.Squarespace]: 'Squarespace',
  [StoreName.Stripe]: 'StripeRelay',
  [StoreName.TikTok]: 'TikTok',
  [StoreName.Walmart]: 'Walmart',
  [StoreName.Weebly]: 'Weebly',
  [StoreName.Wix]: 'Wix',
  [StoreName.WooCommerce]: 'WooCommerce',
  [StoreName.eBay]: 'eBay',
  [StoreName.ePages]: 'ePages',
} as const;

export const filterStoreNameValues = Object.keys(storesFilterValues).reduce<Record<string, string>>(
  (reversed, key) => ({
    ...reversed,
    [storesFilterValues[key as keyof typeof storesFilterValues]]: key,
  }),
  {},
);

export const getStorePlatformFilterValue = (storeName: StoreName): string =>
  storesFilterValues[storeName as keyof typeof storesFilterValues] as string;

export default Store;

export const isStoreLineItemEditable = (shopApp: string): boolean =>
  [storesFilterValues[StoreName.CSV], storesFilterValues[StoreName.Shippo]].includes(
    shopApp as 'CSV_Import' | 'Shippo',
  );
